import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, ApiError, download, fetchBlobUrl } from "../api";
import { Modal } from "../components/Modal";
import { NetTag, netColor } from "../components/NetDirection";
import { fmtDate, fmtINR } from "../fmt";

interface Party {
  id: string;
  name: string;
  kind: string;
}

interface KhaataRow {
  transactionId: string;
  date: string;
  drcr: "dr" | "cr";
  amount: number;
  narration: string | null;
  running: number;
  remark: string | null;
  adjustment: number;
  runningNet: number;
  linkedDocs: number;
  accountId: string;
  accountName: string;
  accountKind: string;
}

interface KhaataResult {
  rows: KhaataRow[];
  totals: { dr: number; cr: number; net: number; adjustment: number; netAdjusted: number };
  scope: "bank" | "all";
}

interface DocumentRow {
  id: string;
  title: string;
  docType: string;
  createdAt: string;
}

interface TxnSourceDoc {
  docId: string;
  title: string;
  docType: string;
  via: "link" | "statement";
}

interface TxnDetail {
  transaction: {
    id: string;
    date: string;
    amount: number;
    drcr: "dr" | "cr";
    narration: string | null;
    notes: string | null;
    source: string;
  };
  account: { id: string; name: string; kind: string; mask: string | null };
  party: { id: string; name: string } | null;
  sourceDocuments: TxnSourceDoc[];
}

type Scope = "bank" | "all";

/** accounts.kind -> the coarse Bank/Card/… label shown in the ledger. */
function typeLabel(kind: string): string {
  if (kind === "bank") return "Bank";
  if (kind === "credit_card" || kind === "paylater") return "Card";
  if (kind === "wallet") return "Wallet";
  if (kind === "cash") return "Cash";
  return kind;
}

export function KhaataDetail() {
  const { id } = useParams<{ id: string }>();
  const [party, setParty] = useState<Party | null>(null);
  const [khaata, setKhaata] = useState<KhaataResult | null>(null);
  const [docs, setDocs] = useState<DocumentRow[]>([]);
  const [scope, setScope] = useState<Scope>("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<TxnDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = () => {
    if (!id) return;
    const qs = new URLSearchParams();
    if (from) qs.set("from", from);
    if (to) qs.set("to", to);
    qs.set("scope", scope);
    Promise.all([
      api.get<Party>(`/api/parties/${id}`),
      api.get<KhaataResult>(`/api/parties/${id}/khaata?${qs}`),
      api.get<DocumentRow[]>(`/api/documents?party_id=${id}`),
    ])
      .then(([p, k, d]) => {
        setParty(p);
        setKhaata(k);
        setDocs(d);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : "failed to load ledger"));
  };

  useEffect(load, [id, from, to, scope]);

  // Refetch just the ledger (not party/docs) after an annotation save so the
  // Net column + totals recompute server-side.
  const reloadKhaata = () => {
    if (!id) return;
    const qs = new URLSearchParams();
    if (from) qs.set("from", from);
    if (to) qs.set("to", to);
    qs.set("scope", scope);
    api
      .get<KhaataResult>(`/api/parties/${id}/khaata?${qs}`)
      .then(setKhaata)
      .catch((err) => setError(err instanceof ApiError ? err.message : "failed to reload ledger"));
  };

  const saveAnnotation = async (
    txnId: string,
    patch: { remark?: string | null; adjustment?: number }
  ) => {
    if (!id) return;
    try {
      await api.patch(`/api/parties/${id}/khaata/${txnId}/annotation`, patch);
      reloadKhaata();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "could not save annotation");
    }
  };

  const onExport = async () => {
    if (!id || !party) return;
    try {
      const qs = new URLSearchParams();
      if (from) qs.set("from", from);
      if (to) qs.set("to", to);
      qs.set("scope", scope);
      await download(`/api/parties/${id}/khaata.xlsx?${qs}`, `ledger-${party.name}.xlsx`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "export failed");
    }
  };

  const openDetail = async (transactionId: string) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const d = await api.get<TxnDetail>(`/api/transactions/${transactionId}/detail`);
      setDetail(d);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "could not load transaction");
    } finally {
      setDetailLoading(false);
    }
  };

  const openDoc = async (docId: string) => {
    try {
      // Bearer token only travels via fetch, never a markup-driven navigation
      // (see api.ts fetchBlobUrl) — so route the PDF through fetch + blob URL.
      const { url } = await fetchBlobUrl(`/api/documents/${docId}/file`);
      window.open(url, "_blank");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "could not open document");
    }
  };

  return (
    <div>
      <h1>
        <Link to="/khaata">Ledger</Link> — {party?.name ?? "..."}
      </h1>
      {error && <div className="error-text">{error}</div>}

      <div className="toolbar">
        <div className="field">
          <label>View</label>
          <div className="actions-inline">
            <button
              type="button"
              className={scope === "bank" ? "primary" : undefined}
              onClick={() => setScope("bank")}
            >
              Bank only
            </button>
            <button
              type="button"
              className={scope === "all" ? "primary" : undefined}
              onClick={() => setScope("all")}
            >
              Bank + Card
            </button>
          </div>
        </div>
        <div className="field">
          <label htmlFor="from">From</label>
          <input id="from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="to">To</label>
          <input id="to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <button type="button" onClick={onExport}>
          Export xlsx
        </button>
      </div>

      {khaata && (
        <div className="card-row" style={{ marginBottom: "1rem" }}>
          <div className="card stat-card">
            <div className="label">Total Dr</div>
            <div className="value">{fmtINR(khaata.totals.dr)}</div>
          </div>
          <div className="card stat-card">
            <div className="label">Total Cr</div>
            <div className="value">{fmtINR(khaata.totals.cr)}</div>
          </div>
          <div className="card stat-card">
            <div className="label">
              Net receivable by you ({scope === "bank" ? "Bank only" : "Bank + Card"})
            </div>
            <div className="value" style={{ color: netColor(khaata.totals.net) }}>
              {fmtINR(khaata.totals.net)}
            </div>
            <div style={{ marginTop: "0.35rem" }}>
              <NetTag value={khaata.totals.net} />
            </div>
          </div>
          <div className="card stat-card">
            <div className="label">Total adjustment</div>
            <div className="value" style={{ color: netColor(khaata.totals.adjustment) }}>
              {fmtINR(khaata.totals.adjustment)}
            </div>
          </div>
          <div className="card stat-card">
            <div className="label">Net total (incl. adjustments)</div>
            <div className="value" style={{ color: netColor(khaata.totals.netAdjusted) }}>
              {fmtINR(khaata.totals.netAdjusted)}
            </div>
            <div style={{ marginTop: "0.35rem" }}>
              <NetTag value={khaata.totals.netAdjusted} />
            </div>
          </div>
        </div>
      )}
      <div className="muted" style={{ margin: "-0.5rem 0 1rem" }}>
        Positive (green) = receivable by you (party owes you); negative (red) =
        payable by you (you owe the party).
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Account</th>
              <th>Type</th>
              <th>Narration</th>
              <th className="num">Dr</th>
              <th className="num">Cr</th>
              <th className="num">Balance (₹)</th>
              <th className="num">Adjustment (+add / −deduct)</th>
              <th className="num">Net total (₹)</th>
              <th>My remark</th>
              <th className="num">Docs</th>
            </tr>
          </thead>
          <tbody>
            {(khaata?.rows ?? []).map((r) => (
              <tr
                key={r.transactionId}
                onClick={() => openDetail(r.transactionId)}
                style={{ cursor: "pointer" }}
                title="View transaction detail"
              >
                <td>{fmtDate(r.date)}</td>
                <td>{r.accountName}</td>
                <td>{typeLabel(r.accountKind)}</td>
                <td>{r.narration ?? "-"}</td>
                <td className="num">{r.drcr === "dr" ? fmtINR(r.amount) : ""}</td>
                <td className="num">{r.drcr === "cr" ? fmtINR(r.amount) : ""}</td>
                <td className="num" style={{ color: netColor(r.running) }}>{fmtINR(r.running)}</td>
                <td className="num" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="number"
                    step="0.01"
                    className="num"
                    defaultValue={r.adjustment || ""}
                    placeholder="0"
                    style={{ width: "6.5rem", textAlign: "right" }}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                    }}
                    onBlur={(e) => {
                      const raw = e.target.value.trim();
                      const v = raw === "" ? 0 : Number(raw);
                      if (!Number.isNaN(v) && v !== r.adjustment) {
                        saveAnnotation(r.transactionId, { adjustment: v });
                      }
                    }}
                  />
                </td>
                <td className="num" style={{ color: netColor(r.runningNet), fontWeight: 600 }}>
                  {fmtINR(r.runningNet)}
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <input
                    type="text"
                    defaultValue={r.remark ?? ""}
                    placeholder="add remark"
                    style={{ width: "12rem" }}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                    }}
                    onBlur={(e) => {
                      const v = e.target.value;
                      if (v !== (r.remark ?? "")) {
                        saveAnnotation(r.transactionId, { remark: v === "" ? null : v });
                      }
                    }}
                  />
                </td>
                <td className="num">{r.linkedDocs || ""}</td>
              </tr>
            ))}
            {khaata && khaata.rows.length === 0 && (
              <tr>
                <td colSpan={11} className="empty-state">
                  No transactions in this range.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <h2>Linked documents</h2>
      <div className="card">
        {docs.length === 0 ? (
          <div className="muted">No documents linked to this party.</div>
        ) : (
          <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
            {docs.map((d) => (
              <li key={d.id}>
                <button
                  type="button"
                  onClick={() => openDoc(d.id)}
                  style={{ padding: 0, border: "none", background: "none", color: "var(--accent)", cursor: "pointer" }}
                >
                  {d.title}
                </button>{" "}
                <span className="muted">({d.docType})</span>
              </li>
            ))}
          </ul>
        )}
        <div className="muted" style={{ marginTop: "0.5rem" }}>
          Link more from the Vault screen.
        </div>
      </div>

      {(detail || detailLoading) && (
        <Modal title="Transaction detail" onClose={() => setDetail(null)} wide>
          {detailLoading || !detail ? (
            <div className="muted">Loading…</div>
          ) : (
            <div>
              <div className="card-row" style={{ marginBottom: "1rem" }}>
                <div className="card stat-card">
                  <div className="label">Date</div>
                  <div className="value">{fmtDate(detail.transaction.date)}</div>
                </div>
                <div className="card stat-card">
                  <div className="label">
                    {detail.transaction.drcr === "dr" ? "Dr (paid by you)" : "Cr (received)"}
                  </div>
                  <div
                    className="value"
                    style={{ color: detail.transaction.drcr === "dr" ? "var(--success)" : "var(--danger)" }}
                  >
                    {fmtINR(detail.transaction.amount)}
                  </div>
                  <div className="muted" style={{ marginTop: "0.35rem", fontSize: "0.75rem" }}>
                    {detail.transaction.drcr === "dr"
                      ? "increases amount receivable by you"
                      : "reduces amount receivable by you"}
                  </div>
                </div>
                <div className="card stat-card">
                  <div className="label">Account</div>
                  <div className="value" style={{ fontSize: "1rem" }}>
                    {detail.account.name}
                    <span className="muted"> ({typeLabel(detail.account.kind)})</span>
                  </div>
                </div>
              </div>

              <table>
                <tbody>
                  <tr>
                    <th style={{ textAlign: "left", width: 140 }}>Party</th>
                    <td>{detail.party?.name ?? "-"}</td>
                  </tr>
                  <tr>
                    <th style={{ textAlign: "left" }}>Narration</th>
                    <td>{detail.transaction.narration ?? "-"}</td>
                  </tr>
                  <tr>
                    <th style={{ textAlign: "left" }}>Notes</th>
                    <td>{detail.transaction.notes ?? "-"}</td>
                  </tr>
                  <tr>
                    <th style={{ textAlign: "left" }}>Source</th>
                    <td>{detail.transaction.source}</td>
                  </tr>
                </tbody>
              </table>

              <h3>Source statements &amp; documents</h3>
              {detail.sourceDocuments.length === 0 ? (
                <div className="muted">No source statement found for this transaction.</div>
              ) : (
                <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
                  {detail.sourceDocuments.map((d) => (
                    <li key={d.docId} style={{ marginBottom: "0.3rem" }}>
                      <button
                        type="button"
                        onClick={() => openDoc(d.docId)}
                        style={{ padding: 0, border: "none", background: "none", color: "var(--accent)", cursor: "pointer" }}
                      >
                        View statement — {d.title}
                      </button>{" "}
                      <span className="muted">
                        ({d.docType}, {d.via === "link" ? "linked" : "matched by period"})
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
